/**
 * Engine-level create/edit parity for optionProp-derived fields.
 *
 * Problem: On edit hydrate, effects are skipped (to avoid clearing phone/SSN/zip).
 * Derived flags like `isOtherCountryPhone` stay at defaults → US-only rules mis-fire.
 *
 * Fix: After hydrate and whenever option metadata arrives, re-apply ONLY `set`
 * from effects that use `optionProp`. Never clear / append / mutualExclude.
 *
 * Authors opt out per effect with `skipDerivedSync: true` when the target field is
 * independently editable (e.g. grant poolId after user overrides plan default).
 */

import type { UseFormReturn } from 'react-hook-form';
import type { FormEffect } from '../types/block';
import type { Compute } from '../types/compute';
import { evaluateCompute } from '../evaluators/computeEvaluator';
import { evaluateCondition, type ConditionContext } from '../evaluators/conditionEvaluator';
import type { FormPrecisionConfig } from '../precision/precisionContract';
import { normalizeProgrammaticNumericValue } from '../precision/precisionContract';

export interface SyncDerivedEffectsParams {
  form: UseFormReturn<Record<string, unknown>>;
  effects: FormEffect[];
  meta: Record<string, unknown>;
  getOptionProp: (field: string, prop: string) => unknown;
  customResolvers?: Record<string, (ctx: ConditionContext) => unknown>;
  /** When set, only sync effects that watch one of these fields. */
  onlyWatchFields?: string[];
  precisionConfig?: FormPrecisionConfig;
}

export function isOptionPropDerivedEffect(effect: FormEffect): boolean {
  if (!effect.set) return false;
  return Object.values(effect.set).some(isOptionPropCompute);
}

function isOptionPropCompute(compute: unknown): compute is Compute & { operator: 'optionProp'; field: string; prop: string } {
  return (
    compute != null &&
    typeof compute === 'object' &&
    (compute as Compute).operator === 'optionProp'
  );
}

function normalizeWatch(watch: FormEffect['watch']): string[] {
  if (Array.isArray(watch)) return watch.filter(Boolean);
  return watch ? [watch] : [];
}

function hasWatchValue(
  watchNames: string[],
  values: Record<string, unknown>,
): boolean {
  return watchNames.some((name) => {
    const v = getNestedValue(values, name);
    return v != null && v !== '';
  });
}

/**
 * True when every optionProp in the effect can resolve (options loaded for current selection).
 * `false` is a valid resolved value; only `undefined` means "not ready yet".
 */
export function canResolveOptionPropSets(
  effect: FormEffect,
  getOptionProp: (field: string, prop: string) => unknown,
): boolean {
  if (!effect.set) return false;
  for (const compute of Object.values(effect.set)) {
    if (!isOptionPropCompute(compute)) continue;
    if (getOptionProp(compute.field, compute.prop) === undefined) {
      return false;
    }
  }
  return true;
}

/**
 * Apply derived optionProp sets only — no clear, no format, no copy.
 * Returns the number of effects that had at least one field written.
 */
export function syncDerivedEffects(params: SyncDerivedEffectsParams): number {
  const {
    form,
    effects,
    meta,
    getOptionProp,
    customResolvers,
    onlyWatchFields,
    precisionConfig,
  } = params;

  const currentValues = form.getValues() as Record<string, unknown>;
  let synced = 0;

  for (const effect of effects) {
    if (!isOptionPropDerivedEffect(effect)) continue;
    if (effect.skipDerivedSync) continue;
    if (!isEffectEnabled(effect, currentValues)) continue;

    const watchNames = normalizeWatch(effect.watch);
    if (watchNames.length === 0) continue;

    if (onlyWatchFields?.length) {
      const overlaps = watchNames.some((w) => onlyWatchFields.includes(w));
      if (!overlaps) continue;
    }

    if (!hasWatchValue(watchNames, currentValues)) continue;
    if (!canResolveOptionPropSets(effect, getOptionProp)) continue;

    if (effect.condition) {
      const ctx: ConditionContext = {
        fieldValue: getNestedValue(currentValues, watchNames[0]),
        values: currentValues,
        meta,
        getOptionProp,
        customResolvers: customResolvers as Record<string, (ctx: ConditionContext) => boolean>,
      };
      if (!evaluateCondition(effect.condition, ctx)) continue;
    }

    const watchMap: Record<string, unknown> = {};
    for (const name of watchNames) {
      watchMap[name] = getNestedValue(currentValues, name);
    }

    let wrote = false;
    for (const [targetField, compute] of Object.entries(effect.set!)) {
      if (!isOptionPropCompute(compute)) {
        // Only sync optionProp targets — other set ops stay user-change-only
        continue;
      }
      const ctx = {
        fieldValue: watchMap[watchNames[0]],
        values: currentValues,
        meta,
        getOptionProp,
        customResolvers,
      };
      const value = evaluateCompute(compute, ctx);
      const normalized = normalizeProgrammaticNumericValue(value, targetField, precisionConfig);
      form.setValue(targetField, normalized, { shouldDirty: false, shouldValidate: true });
      wrote = true;
    }

    if (wrote) synced += 1;
  }

  return synced;
}

function isEffectEnabled(
  effect: FormEffect,
  values: Record<string, unknown>,
): boolean {
  if (!effect.enabled) return true;
  const flag = getNestedValue(values, effect.enabled);
  return (
    flag === true ||
    flag === 'true' ||
    (typeof flag === 'string' && flag.trim() !== '' && flag !== 'false')
  );
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}
